import type { FC, ReactNode } from 'react';
import { Icon } from '@launchpad-ui/icons';

import type { ConversationChromeVariant } from './ConversationMessageCard';
import type { MessageMetaItem } from './MessageMeta';
import { MessageMeta } from './MessageMeta';

import {
  copyAffordance,
  copyAffordanceRedesign,
  investigation,
  investigationBelow,
  investigationBody,
  investigationBodyBare,
  investigationBodyText,
  investigationChevron,
  investigationDetail,
  investigationDetailFlat,
  investigationDuration,
  investigationError,
  investigationExtras,
  investigationFlat,
  investigationFlatOpen,
  investigationHead,
  investigationHeadFlat,
  investigationHeadMain,
  investigationHeadMainButton,
  investigationHeadText,
  investigationIcon,
  investigationInteractive,
  investigationInteractiveHover,
  investigationLabel,
  investigationLabelLoading,
  investigationLabelText,
  investigationText,
  turn,
  turnAgent,
  turnAgentChat,
  turnFlat,
} from '../../internal/components/conversation/ConversationChrome.css';
import { chevron as caretChevron } from './CommandGroupRow.css';

export interface InvestigationRowProps {
  icon?: ReactNode;
  /** Omitted on flat rows whose `detail` carries the whole line. */
  label?: string;
  /** Command / one-line preview shown beside the label. */
  detail?: ReactNode;
  durationLabel?: string;
  isLoading?: boolean;
  isError?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** Chevron after the label that rotates with `expanded`. */
  caret?: boolean;
  children?: ReactNode;
  /** Status chip inside the toggle so it expands/collapses with the row. */
  status?: ReactNode;
  /** Sibling controls outside the toggle (e.g. span icon buttons). */
  headerExtra?: ReactNode;
  /** Supplemental action rendered with the row's metadata caption. */
  footer?: ReactNode;
  meta?: MessageMetaItem[];
  metaTitle?: string;
  className?: string;
  /**
   * `transcript` (default) keeps the agent gutter. `chat` flushes the row so
   * it matches Vega's compact message column.
   */
  variant?: ConversationChromeVariant;
  appearance?: 'card' | 'text';
  bodyAppearance?: 'boxed' | 'bare';
  /** Hide the caption row until the turn is hovered or focused. Pair with `copyHoverGroup` on `className`. */
  hoverRevealCaption?: boolean;
  /** Redesign transcript row: borderless, full width, hover surface. Off keeps the legacy card. */
  flat?: boolean;
}

const HeadMain: FC<{
  icon?: ReactNode;
  label?: string;
  detail?: ReactNode;
  durationLabel?: string;
  isLoading: boolean;
  status?: ReactNode;
  textAppearance: boolean;
  chevron?: 'up' | 'down';
  caret?: boolean;
  expanded?: boolean;
  flat?: boolean;
}> = ({ icon, label, detail, durationLabel, isLoading, status, textAppearance, chevron, caret, expanded, flat }) => (
  <>
    {icon ? <span className={investigationIcon}>{icon}</span> : null}
    {label || !flat ? (
      <span
        className={[
          investigationLabel,
          textAppearance ? investigationLabelText : undefined,
          isLoading ? investigationLabelLoading : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </span>
    ) : null}
    {caret ? (
      <span className={caretChevron} data-open={expanded} aria-hidden>
        <Icon name="chevron-right" size="small" />
      </span>
    ) : null}
    {detail ? (
      <span
        className={[investigationDetail, flat ? investigationDetailFlat : undefined].filter(Boolean).join(' ')}
        title={typeof detail === 'string' ? detail : undefined}
      >
        {detail}
      </span>
    ) : null}
    {durationLabel ? <span className={investigationDuration}>{durationLabel}</span> : null}
    {status}
    {chevron ? (
      <Icon name={chevron === 'up' ? 'chevron-up' : 'chevron-down'} size="small" className={investigationChevron} />
    ) : null}
  </>
);

/**
 * Compact tool / investigation row shared by Vega chat and span Read transcripts.
 * Expand, error, and loading are optional — Figma shows the collapsed chrome only.
 * `status` lives inside the toggle; `headerExtra` stays beside it so nested buttons
 * remain valid HTML.
 */
export const InvestigationRow: FC<InvestigationRowProps> = ({
  icon,
  label,
  detail,
  durationLabel,
  isLoading = false,
  isError = false,
  expanded = false,
  onToggle,
  caret = false,
  children,
  status,
  headerExtra,
  footer,
  meta,
  metaTitle,
  className,
  variant = 'transcript',
  appearance = 'card',
  bodyAppearance = 'boxed',
  hoverRevealCaption,
  flat = false,
}) => {
  const interactive = Boolean(onToggle);
  const hasBody = expanded && Boolean(children);
  const textAppearance = appearance === 'text';
  const chevron = textAppearance && interactive ? (expanded ? 'up' : 'down') : undefined;
  const mainProps = {
    icon,
    label,
    detail,
    durationLabel,
    isLoading,
    status,
    textAppearance,
    chevron,
    caret: caret && interactive,
    expanded,
    flat,
  } as const;
  const turnClass = variant === 'chat' && !flat ? turnAgentChat : turnAgent;

  return (
    <div className={[turn, turnClass, flat ? turnFlat : undefined, className].filter(Boolean).join(' ')}>
      <div
        className={[
          investigation,
          textAppearance ? investigationText : undefined,
          flat ? investigationFlat : undefined,
          flat && hasBody ? investigationFlatOpen : undefined,
          isError ? investigationError : undefined,
          interactive ? investigationInteractive : undefined,
          interactive && flat ? investigationInteractiveHover : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          className={[
            investigationHead,
            textAppearance ? investigationHeadText : undefined,
            flat ? investigationHeadFlat : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {interactive ? (
            <button type="button" className={investigationHeadMainButton} aria-expanded={expanded} onClick={onToggle}>
              <HeadMain {...mainProps} />
            </button>
          ) : (
            <div className={investigationHeadMain}>
              <HeadMain {...mainProps} />
            </div>
          )}
          {headerExtra ? <div className={investigationExtras}>{headerExtra}</div> : null}
        </div>
        {hasBody ? (
          <div
            className={
              textAppearance && bodyAppearance === 'bare'
                ? investigationBodyBare
                : [investigationBody, textAppearance ? investigationBodyText : undefined].filter(Boolean).join(' ')
            }
          >
            {children}
          </div>
        ) : null}
      </div>
      {(flat ? meta?.length : meta) || footer ? (
        <div
          className={[
            investigationBelow,
            hoverRevealCaption ? (flat ? copyAffordanceRedesign : copyAffordance) : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {meta ? <MessageMeta items={meta} title={metaTitle} /> : null}
          {footer}
        </div>
      ) : null}
    </div>
  );
};
